import { AxiosRequestConfig } from "axios";
import { ApiResponse } from "../models/api-response";
import { callExternalApi } from "./external-api.service";
import { getLogger } from "src/utils/logger";
import {
  DeleteDocumentResponse,
  DownloadUrlResponse,
  ListDocumentsParams,
  ListDocumentsResponse,
  SavedDocument,
} from "../models/document-storage.types";

const logger = getLogger("document-storage.service");

const apiServerUrl = process.env.REACT_APP_API_SERVER_URL;
const STORAGE_BASE = `${apiServerUrl}/api/documents/storage`;

const authHeaders = (accessToken: string) => ({
  "content-type": "application/json",
  Authorization: `Bearer ${accessToken}`,
});

/**
 * API service for the Document Storage endpoints.
 *
 * The storage routes return raw JSON bodies (not the `{status: "success"}`
 * envelope the generation endpoints use), so success is determined by the
 * HTTP status: `callExternalApi` surfaces a non-2xx as `response.error`.
 *
 * Every method takes `accessToken` as the last argument, mirroring the other
 * service modules. The backend also serves a preview endpoint and a rename
 * (PATCH) endpoint; neither has a method here because no v1 UI calls them.
 */
export class DocumentStorageService {
  /** GET /api/documents/storage — list the caller's saved documents. */
  static async listDocuments(
    accessToken: string,
    params?: ListDocumentsParams
  ): Promise<ApiResponse<ListDocumentsResponse>> {
    const config: AxiosRequestConfig = {
      url: STORAGE_BASE,
      method: "GET",
      headers: authHeaders(accessToken),
      params: {
        ...(params?.type && { type: params.type }),
        ...(params?.search && { search: params.search }),
      },
    };

    logger.debug("Listing documents");
    return callExternalApi<ListDocumentsResponse>({ config });
  }

  /** GET /api/documents/storage/{id} — single doc with download + preview URLs. */
  static async getDocument(
    documentId: string,
    accessToken: string
  ): Promise<ApiResponse<SavedDocument>> {
    const config: AxiosRequestConfig = {
      url: `${STORAGE_BASE}/${documentId}`,
      method: "GET",
      headers: authHeaders(accessToken),
    };

    logger.debug("Getting document %s", documentId);
    return callExternalApi<SavedDocument>({ config });
  }

  /** GET /api/documents/storage/{id}/download — presigned attachment URL. */
  static async getDownloadUrl(
    documentId: string,
    accessToken: string
  ): Promise<ApiResponse<DownloadUrlResponse>> {
    const config: AxiosRequestConfig = {
      url: `${STORAGE_BASE}/${documentId}/download`,
      method: "GET",
      headers: authHeaders(accessToken),
    };

    logger.debug("Getting download URL for %s", documentId);
    return callExternalApi<DownloadUrlResponse>({ config });
  }

  /** DELETE /api/documents/storage/{id} — delete; returns `{deleted: true}`. */
  static async deleteDocument(
    documentId: string,
    accessToken: string
  ): Promise<ApiResponse<DeleteDocumentResponse>> {
    const config: AxiosRequestConfig = {
      url: `${STORAGE_BASE}/${documentId}`,
      method: "DELETE",
      headers: authHeaders(accessToken),
    };

    logger.debug("Deleting document %s", documentId);
    return callExternalApi<DeleteDocumentResponse>({ config });
  }
}
